import FormHeader from "@/components/FormHeader";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { listJenisPortofolio } from "@/constants";
import api from "@/lib/axios";
import { JenisPortofolio, Option, SysBerkasPortofolio } from "@/types";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { AxiosError } from "axios";
import { Loader2 } from "lucide-react";
import { useState } from "react";
import toast from "react-hot-toast";
import { FaPlus } from "react-icons/fa6";
import { MdModeEdit } from "react-icons/md";
import { RiDeleteBack2Line } from "react-icons/ri";
import { useSearchParams } from "react-router-dom";

const postData = async (newData: SysBerkasPortofolio) => {
  const response = await api.post("/setting/berkas-portofolio/store", newData);
  return response.data;
};

const patchData = async (newData: SysBerkasPortofolio) => {
  const response = await api.patch(
    "/setting/berkas-portofolio/update",
    newData,
  );
  return response.data;
};

const deleteData = async (deletedData: {
  id_sys_berkas_portofolio: string;
}) => {
  const response = await api({
    url: "/setting/berkas-portofolio/delete",
    method: "DELETE",
    data: deletedData,
  });
  return response.data;
};

const listMaxFileSize = [
  256, // 256 KB
  512, // 512 KB
  1024, // 1 MB
  2048, // 2 MB
  5120, // 5 MB
  10240, // 10 MB
  20480, // 20 MB
  51200, // 50 MB
  102400, // 100 MB
];
const listExtension = [
  "pdf", // PDF files
  "doc", // Microsoft Word (older versions)
  "docx", // Microsoft Word (modern versions)
  "xls", // Microsoft Excel (older versions)
  "xlsx", // Microsoft Excel (modern versions)
  "ppt", // Microsoft PowerPoint (older versions)
  "pptx", // Microsoft PowerPoint (modern versions)
  "jpeg", // JPEG image
  "jpg", // JPG image
  "png", // PNG image
  "gif", // GIF image
  "mp4", // MP4 video
  "mov", // QuickTime video
  "avi", // AVI video
  "webm", // WebM video
];
export default function Form({
  method,
  closeModal,
  selectedRow,
}: {
  method: "POST" | "PATCH" | "DELETE";
  closeModal: () => void;
  selectedRow?: SysBerkasPortofolio | null;
}) {
  const isPost = method === "POST";
  const isPatch = method === "PATCH";
  const isDelete = method === "DELETE";

  const [searchParams] = useSearchParams();
  const id_sys_periode_params = searchParams.get("id");

  const [form, setForm] = useState<SysBerkasPortofolio>({
    id_sys_berkas_portofolio: selectedRow?.id_sys_berkas_portofolio || "",
    bidang: selectedRow?.bidang || null,
    nama_jenis_berkas: selectedRow?.nama_jenis_berkas || "",
    slug_jenis_berkas: selectedRow?.slug_jenis_berkas || "",
    keterangan_berkas: selectedRow?.keterangan_berkas || "",
    extension_allowed: selectedRow?.extension_allowed || [],
    max_upload: selectedRow?.max_upload || 0,
    is_active: selectedRow?.is_active ?? false,
  });

  const queryClient = useQueryClient();
  const { mutate, isPending } = useMutation({
    mutationFn: isPost ? postData : isPatch ? patchData : deleteData,
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["listPeriode"] });
      queryClient.invalidateQueries({
        queryKey: ["periode", id_sys_periode_params],
      });
      closeModal();
      toast.success(data?.message || "Success.");
    },
    onError: (error: AxiosError) => {
      const err = error as AxiosError<{ message?: string }>;
      toast.error(
        err?.response?.data?.message || "An unexpected error occurred.",
      );
    },
  });

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    mutate(form);
  };

  if (isDelete)
    return (
      <form onSubmit={handleSubmit} className="flex flex-col gap-y-3">
        <FormHeader
          icon={<RiDeleteBack2Line />}
          title="Delete Data Setting Berkas"
          description={`Apakah anda yakin akan menghapus data?`}
        />

        <div className="flex gap-3">
          <Button
            type="button"
            variant={"outline"}
            disabled={isPending}
            onClick={() => closeModal()}
          >
            Batal
          </Button>
          <Button
            variant={"destructive"}
            className="flex items-center gap-3"
            disabled={isPending}
          >
            {isPending ? (
              <>
                <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                Processing...
              </>
            ) : (
              "Hapus"
            )}
          </Button>
        </div>
      </form>
    );

  return (
    <form onSubmit={handleSubmit}>
      <FormHeader
        icon={method === "POST" ? <FaPlus /> : <MdModeEdit />}
        title={
          isPost
            ? "Insert Data List Berkas Portofolio"
            : "Update Data List Berkas Portofolio"
        }
      />

      <section className="flex flex-col gap-y-3">
        <div>
          <Label>Bidang</Label>
          <Select
            value={form.bidang || ""}
            onValueChange={(value: string) => {
              const bidang = value as JenisPortofolio;
              setForm((prev) => ({
                ...prev,
                bidang,
              }));
            }}
          >
            <SelectTrigger>
              <SelectValue placeholder={"Pilih Bidang"}>
                {form.bidang &&
                  listJenisPortofolio?.find(({ value }) => value == form.bidang)
                    ?.label}
              </SelectValue>
            </SelectTrigger>
            <SelectContent>
              {listJenisPortofolio?.map((option: Option, index: number) => (
                <SelectItem key={index} value={`${option.value}`}>
                  {option.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label>Nama Jenis Berkas</Label>
          <Input
            placeholder="Ketik nama jenis berkas..."
            value={form.nama_jenis_berkas}
            onChange={(event) =>
              setForm((prev) => ({
                ...prev,
                nama_jenis_berkas: event.target.value,
              }))
            }
          />
        </div>
        <div>
          <Label>Keterangan Berkas</Label>
          <Textarea
            value={form.keterangan_berkas}
            placeholder="Keterangan berkas..."
            onChange={(event) =>
              setForm((prev) => ({
                ...prev,
                keterangan_berkas: event.target.value,
              }))
            }
          />
        </div>
        <div>
          <Label>Max File Size</Label>
          <div className="mt-1 flex flex-wrap gap-3">
            {listMaxFileSize.map((size) => {
              return (
                <div key={size} className="flex items-center gap-3">
                  <Checkbox
                    checked={form.max_upload === size}
                    onCheckedChange={() =>
                      setForm((prev) => ({ ...prev, max_upload: size }))
                    }
                  />
                  <Label>{size} KB</Label>
                </div>
              );
            })}
          </div>
        </div>
        <div className="mb-3">
          <Label>Extension Allowed</Label>
          <div className="mt-1 flex flex-wrap gap-3">
            {listExtension.map((extension) => {
              const isChecked = form.extension_allowed?.includes(extension);

              return (
                <div key={extension} className="flex items-center gap-3">
                  <Checkbox
                    checked={isChecked}
                    onCheckedChange={() =>
                      setForm((prev) => ({
                        ...prev,
                        extension_allowed: isChecked
                          ? prev.extension_allowed?.filter(
                              (ext) => ext !== extension,
                            ) || []
                          : [...(prev.extension_allowed || []), extension],
                      }))
                    }
                  />
                  <Label>{extension}</Label>
                </div>
              );
            })}
          </div>
        </div>

        <div>
          <div className="flex gap-3">
            <Switch
              id="is_active"
              checked={form.is_active}
              onCheckedChange={() =>
                setForm((prev) => ({
                  ...prev,
                  is_active: !prev.is_active,
                }))
              }
            />
            <Label htmlFor="is_active">Status (nonaktif/aktif)</Label>
          </div>
        </div>

        <div className="mt-3 flex gap-3">
          <Button
            type="button"
            variant={"outline"}
            onClick={() => closeModal()}
          >
            Batal
          </Button>
          <Button className="flex items-center gap-3" disabled={isPending}>
            {isPending ? (
              <>
                <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                Processing...
              </>
            ) : (
              "Simpan"
            )}
          </Button>
        </div>
      </section>
    </form>
  );
}
